import { useVirtualizer } from '@tanstack/react-virtual';
import { ArrowUp, ChevronDown, ChevronRight } from 'lucide-react';
import { type RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChronologyEventLight } from '@/hooks/useChronology';
import { cn } from '@/lib/utils';
import type { RegionalDateLocale } from '@/utils/date-utils';
import { formatTemporalCompact } from '@/utils/temporal';
import { getCategoryStyle, getEventCategoryName, getTagStyle } from './styles';

const TIMELINE_EVENT_ROW_HEIGHT_PX = 86;
const TIMELINE_YEAR_MARKER_HEIGHT_PX = 26;
const TIMELINE_VIRTUALIZATION_THRESHOLD = 200;

function chronologyYear(date: Date | null | undefined): number | null {
  return date ? date.getUTCFullYear() : null;
}

function chronologyYearKey(date: Date | null | undefined): string | null {
  const year = chronologyYear(date);
  return year === null ? null : year.toString();
}

function timelineRowHasYearMarker(events: ChronologyEventLight[], index: number): boolean {
  const currentYear = chronologyYearKey(events[index]?.temporal_lower);
  const previousYear = index > 0 ? chronologyYearKey(events[index - 1]?.temporal_lower) : null;
  return Boolean(currentYear && currentYear !== previousYear);
}

function timelineRowSize(events: ChronologyEventLight[], index: number): number {
  return TIMELINE_EVENT_ROW_HEIGHT_PX + (timelineRowHasYearMarker(events, index) ? TIMELINE_YEAR_MARKER_HEIGHT_PX : 0);
}

function eventRowIsRendered(container: HTMLDivElement | null, eventId: string): boolean {
  if (!container) return false;
  return !!container.querySelector<HTMLElement>(`[data-event-id="${eventId}"]`);
}

interface TimelineYearNavItem {
  year: string;
  index: number;
}

function timelineYearNavItems(events: ChronologyEventLight[]): TimelineYearNavItem[] {
  const items: TimelineYearNavItem[] = [];
  const seen = new Set<string>();

  events.forEach((ev, index) => {
    const year = chronologyYearKey(ev.temporal_lower);
    if (!year || seen.has(year)) return;
    seen.add(year);
    items.push({ year, index });
  });

  return items;
}

interface TimelineYearJumpProps {
  years: TimelineYearNavItem[];
  activeYear: string | null;
  onSelectYear: (item: TimelineYearNavItem) => void;
}

function TimelineYearJump({ years, activeYear, onSelectYear }: TimelineYearJumpProps) {
  const [open, setOpen] = useState(false);
  if (years.length < 2) return null;
  const label = activeYear ?? years[0]?.year;

  return (
    <div
      className="sticky top-0 z-20 mb-2 flex h-7 w-[52px] items-center justify-end bg-white"
      onBlur={event => {
        const nextTarget = event.relatedTarget;
        if (!(nextTarget instanceof Node) || !event.currentTarget.contains(nextTarget)) {
          setOpen(false);
        }
      }}
    >
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen(value => !value)}
          className={cn(
            'flex items-center justify-end gap-0.5 text-[11px] font-semibold tabular-nums text-gray-400',
            'transition-colors hover:text-gray-600',
          )}
          aria-expanded={open}
          aria-label="Jump to year"
          title="Jump to year"
        >
          <span>{label}</span>
          <ChevronDown className={cn('h-3 w-3 text-gray-400 transition-transform', open && 'rotate-180')} />
        </button>

        {open && (
          <div className="absolute right-0 top-5 w-[52px] rounded-md bg-white py-1 shadow-lg">
            {years.map(item => {
              const isActive = item.year === activeYear;
              return (
                <button
                  type="button"
                  key={item.year}
                  onClick={() => {
                    onSelectYear(item);
                    setOpen(false);
                  }}
                  className={cn(
                    'block w-full px-2 py-1 text-left text-[11px] font-medium tabular-nums transition-colors',
                    isActive ? 'bg-gray-50 text-gray-800' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700',
                  )}
                >
                  {item.year}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

interface TimelineEventButtonProps {
  ev: ChronologyEventLight;
  isSelected: boolean;
  onClick: () => void;
  colorByCategory?: ReadonlyMap<string, string | null>;
  activeCategories?: ReadonlySet<string>;
  excludedTypes?: ReadonlySet<string>;
  isAggregate: boolean;
}

type TimelineTag = ChronologyEventLight['tags'][number];

function tagCategoryName(tag: TimelineTag): string {
  return getEventCategoryName(tag.group_label || tag.group_key || null);
}

function tagMatchesActiveFilters(
  tag: TimelineTag,
  activeCategories?: ReadonlySet<string>,
  excludedTypes?: ReadonlySet<string>,
): boolean {
  const categoryPasses = !activeCategories?.size || activeCategories.has(tagCategoryName(tag));
  return categoryPasses && !excludedTypes?.has(tag.tag_key);
}

function selectWinningGroupKey(
  tags: TimelineTag[],
  activeCategories?: ReadonlySet<string>,
  excludedTypes?: ReadonlySet<string>,
): string | null {
  if (!tags.length) return null;
  const hasTagFilters = Boolean(activeCategories?.size || excludedTypes?.size);
  const countedTags = hasTagFilters
    ? tags.filter(tag => tagMatchesActiveFilters(tag, activeCategories, excludedTypes))
    : tags;
  const candidates = countedTags.length ? countedTags : tags;
  const counts = new Map<string, number>();
  let winningKey: string | null = null;
  let winningCount = 0;

  for (const tag of candidates) {
    const key = tagCategoryName(tag);
    const count = (counts.get(key) ?? 0) + 1;
    counts.set(key, count);
    if (count > winningCount) {
      winningKey = key;
      winningCount = count;
    }
  }

  return winningKey;
}

interface DisplayTagGroup {
  key: string;
  category: string | null | undefined;
  color: string | null | undefined;
  tags: TimelineTag[];
  isWinner: boolean;
}

function groupDisplayTags(tags: TimelineTag[], winningGroupKey: string | null): DisplayTagGroup[] {
  const groups = new Map<string, DisplayTagGroup>();

  tags.forEach(tag => {
    const category = tag.group_label || tag.group_key || null;
    const key = tagCategoryName(tag);
    const existing = groups.get(key);
    if (existing) {
      existing.tags.push(tag);
      return;
    }
    groups.set(key, {
      key,
      category,
      color: tag.group_color,
      tags: [tag],
      isWinner: key === winningGroupKey,
    });
  });

  return Array.from(groups.values())
    .sort((a, b) => Number(b.isWinner) - Number(a.isWinner));
}

function TimelineEventButton({
  ev,
  isSelected,
  onClick,
  colorByCategory,
  activeCategories,
  excludedTypes,
  isAggregate,
}: TimelineEventButtonProps) {
  const winningGroupKey = selectWinningGroupKey(ev.tags || [], activeCategories, excludedTypes);
  const winningTag = ev.tags?.find(tag => tagCategoryName(tag) === winningGroupKey) ?? null;
  const winningCategory = winningTag?.group_label || winningTag?.group_key;
  const tagLabel = ev.tags?.length ? ev.tags.map(tag => tag.label).join(' · ') : 'Untagged';
  const gStyle = getCategoryStyle(winningCategory, colorByCategory);
  const displayTagGroups = groupDisplayTags(ev.tags || [], winningGroupKey);

  return (
    <button
      type="button"
      onMouseDown={event => event.preventDefault()}
      onClick={event => {
        if (event.detail > 0) event.currentTarget.blur();
        onClick();
      }}
      className={cn(
        'w-full overflow-hidden rounded-md border-l-2 px-3 py-2 text-left transition-colors hover:bg-gray-50 group',
        isSelected ? gStyle.activeBorderL : gStyle.borderL,
        isSelected ? 'bg-gray-50' : 'bg-white',
      )}
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="line-clamp-2 text-sm leading-5 text-gray-800">{ev.description}</p>
          <div className="mt-1 flex min-w-0 items-center gap-1.5">
            {displayTagGroups.length ? (
              <p title={tagLabel} className="flex min-w-0 flex-wrap items-center gap-y-0.5 overflow-hidden text-[11px] leading-4">
                {displayTagGroups.flatMap(group => group.tags.map(tag => ({ tag, group }))).map(({ tag, group }, index) => {
                  const tagStyle = getTagStyle(group.color, group.category, colorByCategory);
                  return (
                    <span
                      key={tag.id}
                      className={cn(
                        'inline-flex max-w-full items-center font-medium',
                        group.isWinner ? tagStyle.text : 'text-gray-400',
                      )}
                    >
                      {index > 0 && <span className="mx-1.5 shrink-0 text-[13px] font-semibold leading-4 text-gray-400">·</span>}
                      <span className="min-w-0 truncate">{tag.label}</span>
                    </span>
                  );
                })}
              </p>
            ) : (
              <p title={tagLabel} className={cn('min-w-0 truncate text-[11px] font-medium leading-4', gStyle.text)}>
                {tagLabel}
              </p>
            )}
            {isAggregate && ev.chronology_name && (
              <span className="shrink-0 text-[10px] leading-4 text-gray-400">
                · {ev.chronology_name}
              </span>
            )}
          </div>
        </div>
        <div className="mt-1 flex shrink-0 items-center gap-0.5">
          <ChevronRight className="h-3.5 w-3.5 text-gray-300 opacity-0 transition-opacity group-hover:text-gray-500 group-hover:opacity-100" />
        </div>
      </div>
    </button>
  );
}

interface DatedTimelineRowProps extends TimelineEventButtonProps {
  events: ChronologyEventLight[];
  index: number;
  locale: RegionalDateLocale;
}

function DatedTimelineRow({
  ev,
  events,
  index,
  locale,
  isSelected,
  onClick,
  colorByCategory,
  activeCategories,
  excludedTypes,
  isAggregate,
}: DatedTimelineRowProps) {
  const prevExpr = index > 0 ? events[index - 1].temporal_expression : null;
  const showDate = !prevExpr || ev.temporal_expression !== prevExpr;
  const currentYear = chronologyYearKey(ev.temporal_lower);
  const startsYear = timelineRowHasYearMarker(events, index);
  const compactDate = showDate ? formatTemporalCompact(ev, currentYear ?? null, locale) : null;

  return (
    <div data-event-id={ev.id}>
      {startsYear && currentYear && (
        <div data-year-anchor={currentYear} className="relative flex h-[26px] scroll-mt-9 gap-3">
          <div className="w-[52px] shrink-0 text-right">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">{currentYear}</p>
          </div>
          <div className="flex-1" />
        </div>
      )}
      <div className="relative flex gap-3">
        <div className="w-[52px] shrink-0 pt-2.5 text-right tabular-nums">
          {compactDate ? (
            <p className="text-[11px] font-medium leading-tight text-gray-500">
              {compactDate}
            </p>
          ) : null}
        </div>
        <div className="min-w-0 flex-1 pb-3">
          <TimelineEventButton
            ev={ev}
            isSelected={isSelected}
            onClick={onClick}
            colorByCategory={colorByCategory}
            activeCategories={activeCategories}
            excludedTypes={excludedTypes}
            isAggregate={isAggregate}
          />
        </div>
      </div>
    </div>
  );
}

export interface ChronologyTimelineProps {
  datedEvents: ChronologyEventLight[];
  undatedEvents: ChronologyEventLight[];
  selectedEventId: string | null;
  onSelectEvent: (event: ChronologyEventLight) => void;
  colorByCategory?: ReadonlyMap<string, string | null>;
  activeCategories?: ReadonlySet<string>;
  excludedTypes?: ReadonlySet<string>;
  isAggregate: boolean;
  locale: RegionalDateLocale;
  showUndated: boolean;
  onToggleUndated: () => void;
  onShowUndated: () => void;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
}

export function ChronologyTimeline({
  datedEvents,
  undatedEvents,
  selectedEventId,
  onSelectEvent,
  colorByCategory,
  activeCategories,
  excludedTypes,
  isAggregate,
  locale,
  showUndated,
  onToggleUndated,
  onShowUndated,
  scrollContainerRef,
}: ChronologyTimelineProps) {
  const directSelectionRef = useRef<string | null>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [activeYear, setActiveYear] = useState<string | null>(null);
  const shouldVirtualize = datedEvents.length > TIMELINE_VIRTUALIZATION_THRESHOLD;
  const yearNavItems = useMemo(() => timelineYearNavItems(datedEvents), [datedEvents]);

  const datedVirtualizer = useVirtualizer({
    count: shouldVirtualize ? datedEvents.length : 0,
    getScrollElement: () => scrollContainerRef.current,
    // Measured sizes are cached by key and include the year marker, which depends on the
    // previous event, so the key must change when filtering adds or removes the marker.
    getItemKey: index => {
      const id = datedEvents[index]?.id;
      if (id === undefined) return index;
      return timelineRowHasYearMarker(datedEvents, index) ? `${id}:year` : id;
    },
    estimateSize: index => timelineRowSize(datedEvents, index),
    overscan: 6,
    useFlushSync: false,
  });

  const virtualItems = shouldVirtualize ? datedVirtualizer.getVirtualItems() : [];

  const scrollToYear = useCallback((item: TimelineYearNavItem) => {
    setActiveYear(item.year);

    if (shouldVirtualize) {
      datedVirtualizer.scrollToIndex(item.index, { align: 'start' });
      requestAnimationFrame(() => {
        scrollContainerRef.current?.scrollBy({ top: -36, behavior: 'auto' });
      });
      return;
    }

    scrollContainerRef.current
      ?.querySelector<HTMLElement>(`[data-year-anchor="${item.year}"]`)
      ?.scrollIntoView({ block: 'start', behavior: 'auto' });
  }, [datedVirtualizer, scrollContainerRef, shouldVirtualize]);

  const handleSelectEvent = useCallback((ev: ChronologyEventLight) => {
    directSelectionRef.current = selectedEventId === ev.id ? null : ev.id;
    onSelectEvent(ev);
  }, [selectedEventId, onSelectEvent]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const shouldShow = container.scrollTop > 300;
      setShowScrollTop(prev => (prev === shouldShow ? prev : shouldShow));

      let nextYear = yearNavItems[0]?.year ?? null;
      if (shouldVirtualize) {
        const firstVisible = datedVirtualizer.getVirtualItems()[0];
        nextYear = chronologyYearKey(datedEvents[firstVisible?.index]?.temporal_lower) ?? nextYear;
      } else {
        const containerTop = container.getBoundingClientRect().top;
        const anchors = container.querySelectorAll<HTMLElement>('[data-year-anchor]');
        anchors.forEach(anchor => {
          if (anchor.getBoundingClientRect().top <= containerTop + 96) {
            nextYear = anchor.dataset.yearAnchor ?? nextYear;
          }
        });
      }
      setActiveYear(prev => (prev === nextYear ? prev : nextYear));
    };

    handleScroll();
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [datedEvents, datedVirtualizer, scrollContainerRef, shouldVirtualize, yearNavItems]);

  useEffect(() => {
    if (!selectedEventId) {
      directSelectionRef.current = null;
      return;
    }
    if (directSelectionRef.current === selectedEventId) return;

    const container = scrollContainerRef.current;
    if (eventRowIsRendered(container, selectedEventId)) return;

    const datedIndex = datedEvents.findIndex(ev => ev.id === selectedEventId);
    if (datedIndex >= 0) {
      if (shouldVirtualize) {
        datedVirtualizer.scrollToIndex(datedIndex, { align: 'auto' });
        return;
      }

      requestAnimationFrame(() => {
        container
          ?.querySelector<HTMLElement>(`[data-event-id="${selectedEventId}"]`)
          ?.scrollIntoView({ block: 'nearest' });
      });
      return;
    }

    const undatedIndex = undatedEvents.findIndex(ev => ev.id === selectedEventId);
    if (undatedIndex < 0) return;
    if (!showUndated) {
      onShowUndated();
      return;
    }

    requestAnimationFrame(() => {
      container
        ?.querySelector<HTMLElement>(`[data-event-id="${selectedEventId}"]`)
        ?.scrollIntoView({ block: 'nearest' });
    });
  }, [
    selectedEventId,
    datedEvents,
    undatedEvents,
    shouldVirtualize,
    datedVirtualizer,
    showUndated,
    onShowUndated,
    scrollContainerRef,
  ]);

  return (
    <>
      {datedEvents.length > 0 && (
        <div>
          <TimelineYearJump
            years={yearNavItems}
            activeYear={activeYear}
            onSelectYear={scrollToYear}
          />
          <div className="relative">
            <div className="absolute bottom-0 left-[60px] top-0 w-px bg-gray-200/70" />
            {shouldVirtualize ? (
              <div
                className="relative"
                style={{ height: `${datedVirtualizer.getTotalSize()}px` }}
              >
                {virtualItems.map(virtualItem => {
                  const ev = datedEvents[virtualItem.index];
                  if (!ev) return null;

                  return (
                    <div
                      key={ev.id}
                      ref={datedVirtualizer.measureElement}
                      data-index={virtualItem.index}
                      className="absolute left-0 top-0 w-full"
                      style={{ transform: `translateY(${virtualItem.start}px)` }}
                    >
                      <DatedTimelineRow
                        ev={ev}
                        events={datedEvents}
                        index={virtualItem.index}
                        locale={locale}
                        isSelected={selectedEventId === ev.id}
                        onClick={() => handleSelectEvent(ev)}
                        colorByCategory={colorByCategory}
                        activeCategories={activeCategories}
                        excludedTypes={excludedTypes}
                        isAggregate={isAggregate}
                      />
                    </div>
                  );
                })}
              </div>
            ) : (
              <div>
                {datedEvents.map((ev, index) => (
                  <DatedTimelineRow
                    key={ev.id}
                    ev={ev}
                    events={datedEvents}
                    index={index}
                    locale={locale}
                    isSelected={selectedEventId === ev.id}
                    onClick={() => handleSelectEvent(ev)}
                    colorByCategory={colorByCategory}
                    activeCategories={activeCategories}
                    excludedTypes={excludedTypes}
                    isAggregate={isAggregate}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {undatedEvents.length > 0 && (
        <div className={cn('border-t border-gray-100', datedEvents.length > 0 && 'mt-4 pt-3')}>
          <button type="button" onClick={onToggleUndated} className="mb-2 flex w-full items-center gap-1.5 text-left group">
            {showUndated ? <ChevronDown className="h-3.5 w-3.5 text-gray-400" /> : <ChevronRight className="h-3.5 w-3.5 text-gray-400" />}
            <span className="text-xs font-semibold text-gray-500">Undated events</span>
            <span className="text-xs text-gray-400">{undatedEvents.length}</span>
          </button>
          {showUndated && (
            <div className="space-y-2 pl-1">
              {undatedEvents.map(ev => (
                <div key={ev.id} data-event-id={ev.id} className="pb-2">
                  <TimelineEventButton
                    ev={ev}
                    isSelected={selectedEventId === ev.id}
                    onClick={() => handleSelectEvent(ev)}
                    colorByCategory={colorByCategory}
                    activeCategories={activeCategories}
                    excludedTypes={excludedTypes}
                    isAggregate={isAggregate}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={() => scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
        className={cn(
          'sticky bottom-4 left-full -translate-x-10 h-8 w-8 rounded-full border border-gray-200 bg-white shadow-xs',
          'flex items-center justify-center text-gray-400 hover:border-gray-300 hover:text-gray-600 hover:shadow-sm',
          'transition-all duration-200',
          showScrollTop ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
        )}
        aria-label="Scroll to top"
      >
        <ArrowUp className="h-4 w-4" />
      </button>
    </>
  );
}
